import { NetworkError, ProblemError, type ConversationService, type Id } from '@/model';
import { object } from './decode';
import { commitResponse, requireStore, type ResourceDependencies } from './resources';
import { advancePinsRevision, putConversation, type AccountStore } from './store';

type IntentMethods = Pick<ConversationService, 'setPins' | 'setPinned' | 'setPrefs' | 'mute' | 'unmute'>;
/** Shared per-login ordering for Home, Info and every other caller of pins/preferences. */
export function createConversationIntents(deps: ResourceDependencies): IntentMethods {
  const queues = new Map<string, Promise<void>>();
  let acceptedPins: { db: AccountStore; login: number; ids: Id[]; pinsRevision: number; generation?: number } | undefined;
  type AcceptedNotification = { db: AccountStore; login: number; generation?: number; revision: number; notify: string };
  const acceptedNotifications = new Map<Id, AcceptedNotification>();
  const pinState = (db: AccountStore) => db.transaction('r', db.meta, db.pins, async () => ({ cursor: await db.meta.get('sync'), ids: (await db.pins.orderBy('position').toArray()).map(pin => pin.conversation_id) }));
  const serial = (key: string, operation: (db: AccountStore, assertCurrent: () => void, login: number) => Promise<void>) => {
    const db = requireStore(deps); const login = deps.session.generation();
    const assertCurrent = () => { if (db !== deps.store() || login !== deps.session.generation()) throw new NetworkError('This account session has ended'); };
    const task = (queues.get(key) ?? Promise.resolve()).catch(() => undefined).then(async () => { assertCurrent(); await operation(db, assertCurrent, login); });
    queues.set(key, task); void task.finally(() => { if (queues.get(key) === task) queues.delete(key); }).catch(() => undefined);
    return task;
  };
  const pins = (intent: (ids: Id[]) => Id[]) => serial('pins', async (db, assertCurrent, login) => {
    const before = await pinState(db); const fence = before.cursor;
    const current = acceptedPins?.db === db && acceptedPins.login === login && acceptedPins.pinsRevision === (fence?.pins_revision ?? 0) && acceptedPins.generation === fence?.generation ? acceptedPins.ids : before.ids;
    const ids = [...new Set(intent([...current]))]; if (ids.length > 12) throw new ProblemError({ status: 400, code: 'invalid_request' });
    assertCurrent(); await commitResponse(deps, () => deps.http.put('/v1/pins', { conversation_ids: ids }), { pinsMutation: true }); assertCurrent();
    acceptedPins = await db.transaction('rw', db.meta, async () => {
      const cursor = await db.meta.get('sync'); assertCurrent();
      if (cursor?.generation !== fence?.generation || (cursor?.pins_update_revision ?? 0) !== (fence?.pins_update_revision ?? 0)) return;
      // Acceptance fences older GETs even if the full receipt was deferred for an unrelated stream update.
      const pinsRevision = await advancePinsRevision(db);
      assertCurrent();
      return { db, login, ids, pinsRevision, generation: cursor?.generation };
    });
    assertCurrent();
  });
  const prefs = (id: Id, action: { patch: Record<string, unknown> } | { mute: 'hour' | 'eightHours' | 'week' | 'always' } | { unmute: true }) => serial(`prefs:${id}`, async (db, assertCurrent, login) => {
    const { c, memory, fence } = await db.transaction('r', db.meta, db.conversations, db.conversationIntents, async () => ({ c: await db.conversations.get(id), memory: await db.conversationIntents.get(id), fence: await db.meta.get('sync') }));
    if (!c) throw new ProblemError({ status: 404, code: 'not_found' });
    const pending = acceptedNotifications.get(id);
    const notify = pending?.db === db && pending.login === login && pending.generation === fence?.generation && pending.revision === Number(c._notification_revision ?? 0) ? pending.notify : c.me.notify;
    const prior = notify === 'mentions' ? 'mentions' : notify === 'all' ? 'all' : memory?.notify ?? 'all';
    let patch: Record<string, unknown>;
    if ('patch' in action) patch = action.patch;
    else if ('unmute' in action) patch = { notify: prior, mute_until: null };
    else if (action.mute === 'always') patch = { notify: 'off', mute_until: null };
    else {
      const hours = { hour: 1, eightHours: 8, week: 168 }[action.mute];
      patch = { notify: prior, mute_until: new Date(Date.now() + hours * 3600000).toISOString() };
    }
    const changesNotifications = !('patch' in action) || 'notify' in patch || 'mute_until' in patch;
    let shadow: AcceptedNotification | undefined;
    assertCurrent(); const response = object(await deps.http.patch(`/v1/conversations/${id}/me`, patch)); assertCurrent();
    await db.transaction('rw', db.tables, async () => {
      assertCurrent();
      const held = await db.conversations.get(id);
      const cursor = await db.meta.get('sync');
      const accepted = response.me && typeof response.me === 'object' ? object(response.me) : patch;
      if (await db.erasedConversations.get(id)) return;
      assertCurrent();
      if (changesNotifications) {
        const mode = patch.notify === 'all' || patch.notify === 'mentions' ? patch.notify : prior;
        await db.conversationIntents.put({ id, notify: mode, mute_until: typeof patch.mute_until === 'string' ? patch.mute_until : null });
      }
      assertCurrent();
      if (!held || cursor?.generation !== fence?.generation) return;
      if (cursor?.pts !== fence?.pts) {
        if (Number(held._notification_revision ?? 0) === Number(c._notification_revision ?? 0)) shadow = { db, login, generation: cursor?.generation, revision: Number(held._notification_revision ?? 0), notify: typeof patch.notify === 'string' ? patch.notify : notify };
        await db.fetches.put({ conversation_id: id }); return;
      }
      await putConversation(db, { ...held, me: { ...held.me, ...accepted, read_seq: held.me.read_seq, unread_count: held.me.unread_count, first_unread_seq: held.me.first_unread_seq } });
      assertCurrent();
    });
    assertCurrent();
    if (changesNotifications) { if (shadow) acceptedNotifications.set(id, shadow); else acceptedNotifications.delete(id); }
  });
  return {
    setPins(ids) { const captured = [...ids]; return pins(() => captured); },
    setPinned: (id, pinned) => pins(ids => pinned ? ids.includes(id) ? ids : [...ids, id] : ids.filter(value => value !== id)),
    setPrefs: (id, patch) => prefs(id, { patch: { ...patch } }),
    mute: (id, duration) => prefs(id, { mute: duration }), unmute: id => prefs(id, { unmute: true }),
  };
}
